<template>
  <vue-auth-main>
    <template v-slot:tool>
      <div class="d-flex justify-content-end align-items-center flex-grow-1 mx-3">
        <Search class="te-search-sm" @search="search" />
      </div>
    </template>
    <template v-slot:main>
      <ListFrame ref="listFrame">
        <template v-slot:tabs>
          <slot name="tabs" />
        </template>
        <template v-slot:toolbox>
          <ListToolBox :total-count="totalListCount" :options="listToolBoxOptions" @add="add" @reload="reload" />
        </template>
        <template v-slot:content>
          <ListSelectedBar class="d-none" :selected="selected" :options="listSelectedBarOptions" @cancel="selected = []" @batch-delete="batchDelete" />
          <ListTable
            v-model="selected"
            :listdata="listdata"
            :fields="fields"
            :custom-cell-fields="customCellFields"
            :sort="sort"
            :options="listTableOptions"
            :get-item-index="getItemIndex"
            :get-item-key="getItemKey"
            @data-load="dataLoad"
          >
            <template v-slot:toolbar-content="{ item }">
              <ListModuleTableToolBar v-if="listTableToolBarOptions.tools.length > 0" :options="listTableToolBarOptions" @delete="deleteItem(item)" @copy="cloneItem(item)" @edit="editItem(item)" />
            </template>
          </ListTable>
          <ListPagination v-model="currentPage" :pages="pages" />
        </template>
      </ListFrame>
      <RoleSetting
        v-if="editMode !== ''"
        :role-id="editRoleId"
        :mode="editMode"
        @saved="savedEdit"
        @close="closeEdit"
        @delete="deleteItem"
      />
    </template>
  </vue-auth-main>
</template>

<script>
import {
  ListModuleFrame as ListFrame,
  ListModulePagination as ListPagination,
  ListModuleSelectedBar as ListSelectedBar,
  ListModuleToolBox as ListToolBox,
  ListModuleTable as ListTable,
  ListModuleTableToolBar
} from '@telexpress/vue-list-module';
import RoleSetting from '@/components/Auth/Role/RoleSetting.vue';
import PermissionService from '@/services/PermissionService';
import Search from '@/components/Search.vue';

import { VerifyAuthorizationAsync, GetPermissionsAsync } from '@/services/AuthorizationService';

export default {
  components: {
    ListSelectedBar,
    ListFrame,
    ListToolBox,
    ListPagination,
    ListTable,
    ListModuleTableToolBar,
    Search,
    RoleSetting
  },
  data() {
    return {
      // settings
      listSelectedBarOptions: {
        buttons: ['cancel'],
        tools: []
      },
      listToolBoxOptions: {
        buttons: [],
        tools: ['reload']
      },
      listTableOptions: {
        noLocalSorting: true,
        noSortReset: true
      },
      listTableToolBarOptions: {
        tools: []
      },
      listCount: 20, // 每頁個數

      selected: [],
      sort: {
        key: 'name',
        desc: false
      },
      customCellFields: [
      ],
      currentPage: 1,
      keyword: '',

      listdata: [],
      totalListCount: 0,

      editMode: '',
      editRoleId: ''
    };
  },
  computed: {
    fields() {
      const fields = [
        {
          key: 'selected', label: '', class: 'align-middle text-center', thClass: 'border-0', thStyle: { width: '48px' }
        },
        {
          key: 'name', label: this.$t('auth.roleColumn.name'), class: 'align-middle', thClass: 'border-0', sortable: true
        },
        {
          key: 'desc', label: this.$t('auth.roleColumn.desc'), class: 'align-middle text-nowrap', thClass: 'border-0'
        },
        {
          key: 'userCount', label: this.$t('auth.roleColumn.userCount'), class: 'text-right align-middle', thClass: 'border-0', sortable: true
        },
        {
          key: 'creater', label: this.$t('auth.roleColumn.creater'), class: 'align-middle', thClass: 'border-0'
        },
        {
          key: 'updater', label: this.$t('auth.roleColumn.updater'), class: 'align-middle', thClass: 'border-0'
        }
      ];

      if (!this.editMode) {
        fields.push({
          key: 'toolbar', label: '', class: 'align-middle text-nowrap td-toolbar', tdClass: 'position-relative', thClass: 'border-0'
        });
      }

      return fields;
    },
    pages() {
      const pages = Math.ceil(this.totalListCount / this.listCount);
      return pages > 0 ? pages : 1;
    }
  },
  watch: {
    currentPage() {
      this.LoadListDataAsync();
    },
    sort(val, oldVal) {
      if (val.key !== oldVal.key || val.desc !== oldVal.desc) {
        this.LoadListDataAsync();
      }
    }
  },
  created() {
    // 設置操作權限
    this.setPermissions().then(this.updatePermissionButtons);
    this.LoadListDataAsync();
  },
  methods: {
    // 設置操作權限
    async setPermissions() {
      const feature = 'ManageRole';
      const verified = await VerifyAuthorizationAsync(feature);
      if (verified) {
        const permissions = await GetPermissionsAsync(feature);
        this.$store.commit('auth/setPermissions', permissions);
      } else {
        this.$store.commit('auth/setPermissions', []);
      }
    },
    // 更新操作權限功能顯示
    updatePermissionButtons() {
      const permissions = this.$store.getters['auth/permissions'];

      // 編輯
      if (permissions.includes('edit')) {
        this.listToolBoxOptions.buttons.push('add');
        this.listTableToolBarOptions.tools.push('edit');
        this.listTableToolBarOptions.tools.push('copy');
      }
      // 刪除
      if (permissions.includes('delete')) {
        this.listSelectedBarOptions.tools.push('delete');
        this.listTableToolBarOptions.tools.push('delete');
      }
    },
    async LoadListDataAsync() {
      await this.$loading(async () => {
        const offset = (this.currentPage - 1) * this.listCount;
        const result = await PermissionService.GetRolesAsync(this.keyword, this.listCount, offset, this.sort.key, this.sort.desc);
        this.listdata = (result || {}).data || [];
        this.totalListCount = (result || {}).totalCount || 0;
      });
    },
    getItemIndex(item) {
      return this.selected.indexOf(item.id);
    },
    getItemKey(item) {
      return item.id;
    },
    add() {
      this.editMode = 'add';
      this.openEdit();
    },
    reload() {
      this.LoadListDataAsync();
    },
    async batchDelete() {
      const confirm = await this.$popup.confirmAsync(this.$t('auth.roleEdit.confirmDelete'));
      if (confirm) {
        await this.$loading(async () => {
          try {
            await PermissionService.DeleteRolesAsync(this.selected);
            this.$notify.deleteSuccess();
            this.selected = [];
            this.LoadListDataAsync();
          } catch (err) {
            this.$notify.deleteFailed();
          }
        });
      }
    },
    editItem(item) {
      this.editMode = 'edit';
      this.openEdit(item.id);
    },
    cloneItem(item) {
      this.editMode = 'clone';
      this.openEdit(item.id);
    },
    async deleteItem(item) {
      const confirm = await this.$popup.confirmAsync(this.$t('auth.roleEdit.confirmDelete'));
      if (confirm) {
        await this.$loading(async () => {
          try {
            await PermissionService.DeleteRoleAsync(item.id);
            this.$notify.deleteSuccess();
            const index = this.selected.indexOf(item.id);
            if (index > -1) this.selected.splice(index, 1);
            if (this.editRoleId === item.id) this.closeEdit();
            this.LoadListDataAsync();
          } catch (err) {
            this.$notify.deleteFailed();
          }
        });
      }
    },
    dataLoad(options) {
      this.sort = {
        key: options.sortBy || '',
        desc: options.sortDesc || false
      };
    },
    search(val) {
      this.keyword = val;
      this.LoadListDataAsync();
    },

    // edit
    savedEdit() {
      this.closeEdit();
      this.LoadListDataAsync();
    },
    openEdit(id = '') {
      this.editRoleId = id;
    },
    closeEdit() {
      this.editMode = '';
    }
  }
};
</script>
